import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { Modal } from "./Modal";
import {
  RECIPE_IMPORT_EXAMPLE_JSON,
  RECIPE_IMPORT_FIELD_HELP,
  analyzeRecipeImport,
  buildResolvePlan,
  parseRecipeImportJson,
  resolveIngredientPackageSize,
  runRecipeImport,
  type ImportAnalysis,
  type RecipeImportPayload,
  type RecipeImportRunResult,
  type UnresolvedIngredient,
} from "../lib/recipeImport";
import type { Ingredient } from "../types";

type ImportStep = "json" | "mapping" | "create" | "done";

export function RecipeImporter({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<ImportStep>("json");
  const [json, setJson] = useState("");
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);
  const [userMappings, setUserMappings] = useState<Record<string, string>>({});
  const [result, setResult] = useState<RecipeImportRunResult | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const ingredientsQ = useQuery({
    queryKey: ["ingredients"],
    queryFn: () => api.get<{ ingredients: Ingredient[] }>("/api/ingredients"),
    enabled: open,
  });

  const catalog = ingredientsQ.data?.ingredients ?? [];

  const unmappedKeys = useMemo(() => {
    if (!analysis) return new Set<string>();
    return new Set(analysis.unmatched.filter((u) => !userMappings[u.key]?.trim()).map((u) => u.key));
  }, [analysis, userMappings]);

  const importMutation = useMutation({
    mutationFn: async (params: {
      recipes: RecipeImportPayload[];
      analysis: ImportAnalysis;
      mappings: Record<string, string>;
      createKeys: Set<string>;
    }) => {
      const plan = buildResolvePlan(params.analysis, params.mappings, params.createKeys);
      return runRecipeImport(params.recipes, plan, {
        createIngredient: (body) => api.post("/api/ingredients", body),
        createRecipe: (body) => api.post("/api/recipes", body),
      });
    },
    onSuccess: (res) => {
      setResult(res);
      setStep("done");
      qc.invalidateQueries({ queryKey: ["recipes"] });
      qc.invalidateQueries({ queryKey: ["ingredients"] });
    },
  });

  const reset = () => {
    setStep("json");
    setJson("");
    setParseErrors([]);
    setAnalysis(null);
    setUserMappings({});
    setResult(null);
    setShowHelp(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAnalyze = () => {
    const parsed = parseRecipeImportJson(json);
    if (!parsed.ok) {
      setParseErrors(parsed.errors);
      return;
    }
    setParseErrors([]);
    const next = analyzeRecipeImport(parsed.recipes, catalog);

    if (next.unmatched.length === 0) {
      const plan = buildResolvePlan(next, {}, new Set());
      importMutation.mutate({ recipes: next.recipes, analysis: next, mappings: {}, createKeys: new Set() });
      return;
    }

    setAnalysis(next);
    setUserMappings({});
    setStep("mapping");
  };

  const handleMappingContinue = () => {
    if (!analysis) return;
    if (unmappedKeys.size > 0) {
      setStep("create");
      return;
    }
    importMutation.mutate({
      recipes: analysis.recipes,
      analysis,
      mappings: userMappings,
      createKeys: new Set(),
    });
  };

  const handleCreateAndImport = () => {
    if (!analysis) return;
    importMutation.mutate({
      recipes: analysis.recipes,
      analysis,
      mappings: userMappings,
      createKeys: unmappedKeys,
    });
  };

  const footer = (() => {
    if (step === "done") {
      return (
        <button className="btn-primary" onClick={handleClose}>
          Zavřít
        </button>
      );
    }
    if (step === "json") {
      return (
        <>
          <button className="btn-ghost" onClick={handleClose}>
            Zrušit
          </button>
          <button
            className="btn-primary flex items-center gap-1"
            disabled={!json.trim() || ingredientsQ.isLoading || importMutation.isPending}
            onClick={handleAnalyze}
          >
            <span aria-hidden>➡️</span>
            <span>{importMutation.isPending ? "Importuji…" : "Pokračovat"}</span>
          </button>
        </>
      );
    }
    if (step === "mapping") {
      return (
        <>
          <button className="btn-ghost" onClick={() => setStep("json")}>
            ← Zpět
          </button>
          <button
            className="btn-primary"
            disabled={importMutation.isPending}
            onClick={handleMappingContinue}
          >
            {unmappedKeys.size > 0 ? "Pokračovat k vytvoření nových" : "Importovat"}
          </button>
        </>
      );
    }
    if (step === "create") {
      return (
        <>
          <button className="btn-ghost" onClick={() => setStep("mapping")}>
            ← Zpět k mapování
          </button>
          <button
            className="btn-primary flex items-center gap-1"
            disabled={unmappedKeys.size === 0 || importMutation.isPending}
            onClick={handleCreateAndImport}
          >
            <span aria-hidden>📥</span>
            <span>{importMutation.isPending ? "Importuji…" : "Vytvořit a importovat"}</span>
          </button>
        </>
      );
    }
    return null;
  })();

  const title =
    step === "mapping"
      ? "Mapování surovin"
      : step === "create"
        ? "Nové suroviny ke vytvoření"
        : "Import receptu z JSON";

  return (
    <Modal open={open} onClose={handleClose} title={title} wide footer={footer}>
      {importMutation.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 mb-4">
          <p className="font-medium">Import se nezdařil</p>
          <p className="mt-1">
            {importMutation.error instanceof ApiError
              ? importMutation.error.message
              : importMutation.error instanceof Error
                ? importMutation.error.message
                : "Zkuste to prosím znovu."}
          </p>
        </div>
      )}
      {step === "done" && result ? (
        <ImportSuccess result={result} />
      ) : step === "mapping" && analysis ? (
        <MappingStep
          analysis={analysis}
          catalog={catalog}
          userMappings={userMappings}
          onMappingChange={(key, ingredientId) =>
            setUserMappings((prev) => {
              const next = { ...prev };
              if (ingredientId) next[key] = ingredientId;
              else delete next[key];
              return next;
            })
          }
        />
      ) : step === "create" && analysis ? (
        <CreateStep analysis={analysis} unmappedKeys={unmappedKeys} userMappings={userMappings} />
      ) : (
        <JsonStep
          json={json}
          setJson={setJson}
          parseErrors={parseErrors}
          setParseErrors={setParseErrors}
          showHelp={showHelp}
          setShowHelp={setShowHelp}
          importError={importMutation.isError && parseErrors.length === 0}
        />
      )}
    </Modal>
  );
}

function JsonStep({
  json,
  setJson,
  parseErrors,
  setParseErrors,
  showHelp,
  setShowHelp,
  importError,
}: {
  json: string;
  setJson: (v: string) => void;
  parseErrors: string[];
  setParseErrors: (v: string[]) => void;
  showHelp: boolean;
  setShowHelp: (v: boolean | ((prev: boolean) => boolean)) => void;
  importError: boolean;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Vložte JSON jednoho receptu nebo pole receptů. Suroviny se nejdřív pokusí spárovat s katalogem;
        nespárované můžete v dalším kroku mapovat na existující, nebo je nechat vytvořit jako nové.
      </p>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={() => {
            setJson(RECIPE_IMPORT_EXAMPLE_JSON);
            setParseErrors([]);
          }}
        >
          Vložit ukázku
        </button>
        <a
          href="/recipe-import-example.json"
          download="recipe-import-example.json"
          className="btn-ghost text-xs inline-flex items-center"
        >
          Stáhnout šablonu
        </a>
        <button type="button" className="btn-ghost text-xs" onClick={() => setShowHelp((v) => !v)}>
          {showHelp ? "Skrýt popis polí" : "Popis polí JSON"}
        </button>
      </div>

      {showHelp && (
        <div className="rounded-lg border bg-slate-50 p-3 text-xs space-y-2 max-h-48 overflow-y-auto">
          <p className="font-medium text-slate-700">Povolené hodnoty mealTypes:</p>
          <p className="text-slate-600 font-mono">snidane, svacina, obed, svacina2, vecere</p>
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="pr-2 pb-1">Pole</th>
                <th className="pr-2 pb-1">Pov.</th>
                <th className="pb-1">Význam</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {RECIPE_IMPORT_FIELD_HELP.map((row) => (
                <tr key={row.field} className="align-top">
                  <td className="pr-2 py-0.5 font-mono whitespace-nowrap">{row.field}</td>
                  <td className="pr-2 py-0.5">{row.required ? "ano" : "ne"}</td>
                  <td className="py-0.5">{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <label className="label" htmlFor="recipe-import-json">
          JSON receptu
        </label>
        <textarea
          id="recipe-import-json"
          className="input font-mono text-xs min-h-[280px] resize-y"
          placeholder='{"name": "Název receptu", "servings": 4, "ingredients": […]}'
          value={json}
          onChange={(e) => {
            setJson(e.target.value);
            setParseErrors([]);
          }}
          spellCheck={false}
        />
      </div>

      {parseErrors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="font-medium mb-1">Chyba ve formátu JSON:</p>
          <ul className="list-disc pl-5 space-y-0.5">
            {parseErrors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {importError && (
        <p className="text-sm text-red-600">
          Import se nezdařil. Zkontrolujte JSON a zkuste to znovu.
        </p>
      )}
    </div>
  );
}

function MappingStep({
  analysis,
  catalog,
  userMappings,
  onMappingChange,
}: {
  analysis: ImportAnalysis;
  catalog: Ingredient[];
  userMappings: Record<string, string>;
  onMappingChange: (key: string, ingredientId: string) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Níže jsou suroviny z JSON, které v katalogu nebyly nalezeny podle názvu. Pokuste se je přiřadit k
        existující položce. Co nenamapujete, nabídneme k vytvoření v dalším kroku.
      </p>

      {analysis.autoMatched.length > 0 && (
        <div className="rounded-lg border bg-green-50 p-3 text-sm">
          <p className="font-medium text-green-900 mb-1">
            Automaticky spárováno ({analysis.autoMatched.length})
          </p>
          <ul className="text-green-800 space-y-0.5">
            {analysis.autoMatched.map((m) => (
              <li key={m.key}>
                <span className="font-medium">{m.importName}</span>
                <span className="text-green-700"> → {m.catalogName}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3 max-h-[360px] overflow-y-auto">
        {analysis.unmatched.map((u) => (
          <UnresolvedRow
            key={u.key}
            item={u}
            catalog={catalog}
            mappedId={userMappings[u.key] ?? ""}
            onChange={(id) => onMappingChange(u.key, id)}
          />
        ))}
      </div>
    </div>
  );
}

function UnresolvedRow({
  item,
  catalog,
  mappedId,
  onChange,
}: {
  item: UnresolvedIngredient;
  catalog: Ingredient[];
  mappedId: string;
  onChange: (ingredientId: string) => void;
}) {
  const sorted = useMemo(() => sortCatalogForMapping(catalog, item.name), [catalog, item.name]);
  const mapped = catalog.find((i) => i.id === mappedId);

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-medium text-slate-900">{item.name}</span>
        <span className="text-sm text-slate-500">
          {item.unit}
          {item.byWeight || item.packageSize === 0
            ? " · na váhu"
            : item.packageSize
              ? ` · balení ${item.packageSize} ${item.unit}`
              : ""}
        </span>
        {item.usedInRecipes.length > 0 && (
          <span className="text-xs text-slate-400">
            v receptu: {item.usedInRecipes.join(", ")}
          </span>
        )}
      </div>
      <select
        className="input"
        value={mappedId}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— nechat k vytvoření v dalším kroku —</option>
        {sorted.map((i) => (
          <option key={i.id} value={i.id}>
            {i.name} ({i.unit})
          </option>
        ))}
      </select>
      {mapped && (
        <p className="text-xs text-brand-700">
          Namapováno na: <strong>{mapped.name}</strong>
        </p>
      )}
    </div>
  );
}

function CreateStep({
  analysis,
  unmappedKeys,
  userMappings,
}: {
  analysis: ImportAnalysis;
  unmappedKeys: Set<string>;
  userMappings: Record<string, string>;
}) {
  const toCreate = analysis.unmatched.filter((u) => unmappedKeys.has(u.key));

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Tyto suroviny nebyly namapovány na existující položky. Při importu se vytvoří v katalogu podle údajů
        z JSON.
      </p>

      {Object.keys(userMappings).length > 0 && (
        <p className="text-sm text-green-800">
          {Object.keys(userMappings).length}{" "}
          {Object.keys(userMappings).length === 1 ? "surovina byla" : "surovin bylo"} namapováno na existující
          katalog.
        </p>
      )}

      <ul className="divide-y border rounded-lg max-h-[360px] overflow-y-auto">
        {toCreate.map((u) => {
          const data = analysis.ingredientDataByKey.get(u.key);
          const pkg = data ? resolveIngredientPackageSize(data) : 1;
          return (
            <li key={u.key} className="px-4 py-3 text-sm">
              <div className="font-medium">{u.name}</div>
              <div className="text-slate-500 mt-0.5">
                jednotka: {u.unit}
                {pkg === 0 ? " · na váhu" : ` · balení: ${pkg} ${u.unit}`}
              </div>
              {u.usedInRecipes.length > 0 && (
                <div className="text-xs text-slate-400 mt-0.5">recept: {u.usedInRecipes.join(", ")}</div>
              )}
            </li>
          );
        })}
      </ul>

      {toCreate.length === 0 && (
        <p className="text-sm text-slate-500">Všechny suroviny jsou namapované — vraťte se a spusťte import.</p>
      )}
    </div>
  );
}

function sortCatalogForMapping(catalog: Ingredient[], importName: string): Ingredient[] {
  const q = importName.trim().toLocaleLowerCase("cs");
  return [...catalog].sort((a, b) => {
    const aName = a.name.toLocaleLowerCase("cs");
    const bName = b.name.toLocaleLowerCase("cs");
    const aScore =
      aName === q ? 0 : aName.includes(q) || q.includes(aName) ? 1 : 2;
    const bScore =
      bName === q ? 0 : bName.includes(q) || q.includes(bName) ? 1 : 2;
    if (aScore !== bScore) return aScore - bScore;
    return a.name.localeCompare(b.name, "cs");
  });
}

function ImportSuccess({ result }: { result: RecipeImportRunResult }) {
  const count = result.recipeIds.length;
  return (
    <div className="space-y-3 text-sm">
      <p className="text-green-800 font-medium flex items-center gap-2">
        <span aria-hidden>✅</span>
        <span>
          Import dokončen — {count} {count === 1 ? "recept" : count < 5 ? "recepty" : "receptů"}.
        </span>
      </p>
      {result.createdIngredients.length > 0 && (
        <div>
          <p className="text-slate-600 font-medium">Nově vytvořené suroviny ({result.createdIngredients.length}):</p>
          <ul className="list-disc pl-5 text-slate-700 mt-1">
            {result.createdIngredients.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      {result.reusedIngredients.length > 0 && (
        <div>
          <p className="text-slate-600 font-medium">
            Použité existující suroviny ({result.reusedIngredients.length}):
          </p>
          <ul className="list-disc pl-5 text-slate-700 mt-1">
            {result.reusedIngredients.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-slate-500 text-xs">
        Recepty najdete v tabulce katalogu. Upravit je můžete po otevření detailu.
      </p>
    </div>
  );
}
